
class IntegratorDetailViewController extends MUIViewController
{
    static newInstance(): IntegratorDetailViewController {
        let vc = new IntegratorDetailViewController("integrator-detail-view");
        vc.initWithResource("layout/setup/IntegratorDetailView.html");
        return vc;
    }

    private addButton: MUIButton = null;
    private backButton: MUIButton = null;
    private saveButton: MUIButton = null;
    private testButton: MUIButton = null;
    private setupJobsButton: MUIButton = null;
    private historyButton: MUIButton = null;
    private healthLabel: MUILabel = null;
    private titleLabel: MUILabel = null;
    private nameTextField: MUITextField = null;
    private typeLabel: MUILabel = null;
    private tableView: UITableView = null;
    private connectionTableView: UITableView = null;

    // The integrator's driver manifest (from GET /integrator/drivers) and the connection
    // rows rendered from it: the template keys in declared order, then any extra keys the
    // venue already carries. Template keys are seeded EMPTY into configValues on open —
    // visible and editable, never overwriting.
    private _driver: any = null;
    private _connectionKeys: any[] = [];

    private _integrator: Integrator = null;
    set integrator(item: Integrator) {
        this._integrator = item;
        this.updateUI();
    }

    viewDidLoad() {
        super.viewDidLoad();

        this.backButton = MUIOutlet(this, "back-btn", "MUIButton");
        this.backButton.setAction(this, function() {
            this.navigationController.popViewController(true);
        });

        this.saveButton = MUIOutlet(this, "save-btn", "MUIButton");
        this.saveButton.setAction(this, function() {
            DBHelper.saveMainContext();
        });

        this.titleLabel = MUIOutlet(this, "title-lbl", "MUILabel");

        this.nameTextField = MUIOutlet(this, "name-tf", "MUITextField");
        this.nameTextField.setOnChangeText(this, function(tf: MUITextField, value: string) {
            if (this._integrator != null) {
                this._integrator.name = value;
            }
        });

        this.typeLabel = MUIOutlet(this, "type-lbl", "MUILabel");

        this.addButton = MUIOutlet(this, "add-btn", "MUIButton");
        this.addButton.setAction(this, function() {
            this.addConfig();
        });

        this.tableView = MUIOutlet(this, "table-view", "UITableView");
        this.tableView.dataSource = this;
        this.tableView.delegate = this;

        this.connectionTableView = MUIOutlet(this, "connection-table-view", "UITableView");
        this.connectionTableView.dataSource = this;
        this.connectionTableView.delegate = this;

        this.testButton = MUIOutlet(this, "test-btn", "MUIButton");
        this.testButton.setAction(this, function(this:IntegratorDetailViewController) {
            this.testConnection();
        });

        this.setupJobsButton = MUIOutlet(this, "setup-jobs-btn", "MUIButton");
        this.setupJobsButton.setAction(this, function(this:IntegratorDetailViewController) {
            this.setupJobs();
        });

        this.historyButton = MUIOutlet(this, "history-btn", "MUIButton");
        this.historyButton.setAction(this, function(this:IntegratorDetailViewController) {
            if (this._integrator == null) return;
            let vc = IntegratorHistoryViewController.newInstance();
            vc.integrator = this._integrator;
            this.navigationController.pushViewController(vc, true);
        });

        this.healthLabel = MUIOutlet(this, "health-lbl", "MUILabel");

        this.updateUI();
    }

    viewWillAppear(animate?) {
        super.viewWillAppear(animate);
        this.fetchedResultsController = null;
        this.updateUI();
    }

    private updateUI() {
        if (this.viewIsLoaded == false || this._integrator == null) return;

        this.titleLabel.text = this._integrator.name;
        this.loadDriver();
        this.nameTextField.text = this._integrator.name;
        this.typeLabel.text = this._integrator.type || "-";

        this.tableView.reloadData();
    }

    numberOfSections(tableview) {
        return this.fetchedResultsController.sections.length;
    }

    numberOfRowsInSection(tableview, section) {
        if (tableview === this.connectionTableView) return this._connectionKeys.length;
        let sec = this.fetchedResultsController.sections[section];
        return sec.numberOfObjects();
    }

    cellAtIndexPath(tableview, indexPath: MIOIndexPath) {
        if (tableview === this.connectionTableView) {
            let entry = this._connectionKeys[indexPath.row];
            let identifier = entry.secret == true ? "IntegratorConnectionSecretCell" : "IntegratorConnectionKeyCell";
            let kcell = tableview.dequeueReusableCellWithIdentifier(identifier) as IntegratorConnectionKeyCell;
            kcell.setKey(entry.key, entry.optional == true, this._integrator);
            return kcell;
        }

        let cell = tableview.dequeueReusableCellWithIdentifier("IntegratorConfigCell") as IntegratorConfigCell;
        let item = this.fetchedResultsController.objectAtIndexPath(indexPath) as IntegratorConfig;
        cell.item = item;
        return cell;
    }

    didSelectCellAtIndexPath(tableView, indexPath: MIOIndexPath) {
        if (tableView === this.connectionTableView) return;
        let item = this.fetchedResultsController.objectAtIndexPath(indexPath) as IntegratorConfig;
        if (item) {
            let vc = IntegratorConfigDetailViewController.newInstance();
            vc.integratorType = this._integrator.type;
            vc.config = item;
            this.navigationController.pushViewController(vc, true);
        }
    }

    editingStyleForRowAtIndexPath(tableView: UITableView, indexPath: MIOIndexPath) {
        if (tableView === this.connectionTableView) return UITableViewCellEditingStyle.None;
        return UITableViewCellEditingStyle.Delete;
    }

    commitEditingStyleForRowAtIndexPath(tableView, editingStyle: UITableViewCellEditingStyle, indexPath: MIOIndexPath) {
        if (editingStyle == UITableViewCellEditingStyle.Delete) {
            let item = this.fetchedResultsController.objectAtIndexPath(indexPath) as IntegratorConfig;
            DBHelper.deleteObjectFromMainContext(item, true);
        }
    }

    private _fetchedResultsController: MIOFetchedResultsController = null;
    set fetchedResultsController(value) {
        if (value == null && this._fetchedResultsController != null)
            this._fetchedResultsController.delegate = null;
        this._fetchedResultsController = value;
    }

    get fetchedResultsController() {
        if (this._fetchedResultsController != null) return this._fetchedResultsController;
        if (this._integrator == null) return null;

        let ad = MUIWebApplication.sharedInstance().delegate;
        let sortDescriptors = [MIOSortDescriptor.sortDescriptorWithKey("name", true)];
        let predicate = "integrator.identifier == '" + this._integrator.identifier + "'";
        let fetchRequest = DBHelper.listFetchRequestWithEntityName("IntegratorConfig", sortDescriptors, predicate);

        let frc = new MIOFetchedResultsController();
        frc.initWithFetchRequest(fetchRequest, ad.managedObjectContext, null);
        frc.delegate = this;
        frc.performFetch();

        this._fetchedResultsController = frc;
        return this._fetchedResultsController;
    }

    controllerDidChangeContent(controller) {
        this.tableView.reloadData();
    }

    private loadDriver() {
        DriverManifests.load(() => this.driverDidLoad());
    }

    private driverDidLoad() {
        if (this._integrator == null) return;
        this._driver = DriverManifests.find(this._integrator.type);

        let template: any[] = this._driver != null ? (this._driver["connection"] ?? []) : [];

        // Seed the template keys EMPTY so they become visible and editable — never
        // overwriting a value the venue already has. Assign only when something was
        // actually missing, so opening the screen doesn't dirty the context.
        let values = this._integrator.configValues || {};
        let seeded = false;
        for (let entry of template) {
            if (values[entry["key"]] == null) { values[entry["key"]] = ""; seeded = true; }
        }
        if (seeded) this._integrator.configValues = values;

        // Rows: the template in declared order, then any extra keys the venue carries
        // (their names stay visible; "role" and friends belong to configs, not here).
        let known = template.map((e: any) => e["key"]);
        let extras = Object.keys(values).filter(k => known.indexOf(k) < 0).sort()
                           .map(k => ({ key: k, secret: false, optional: false }));
        this._connectionKeys = template.map((e: any) => ({ key: e["key"], secret: e["secret"] == true, optional: e["optional"] == true }))
                                       .concat(extras);

        this.testButton.setHidden(this._driver == null);
        this.setupJobsButton.setHidden(this._driver == null || (this._driver["jobs"] ?? []).length == 0);
        this.historyButton.setHidden(this._driver == null || (this._driver["jobs"] ?? []).length == 0);
        this.connectionTableView.reloadData();
        this.loadHealth();
    }

    // The health strip: one line, worst problem first — HALTED with the blocking
    // document, missing connection, missing mapping, missing job, error count, READY.
    private loadHealth() {
        if (this._integrator == null || this._driver == null) { this.healthLabel.text = ""; return; }
        let ws = MUIWebApplication.sharedInstance().delegate.webService;
        ws.integratorHealth(this._integrator, (health: any, error: any) => {
            this.healthLabel.text = error != null ? "" : IntegratorHealthSummary.text(health);
        });
    }

    // Creates the integrator's missing standard jobs from its driver's manifest
    // (POST /integrator/jobs/setup). The report says what was created, linked and
    // deactivated; new configs arrive with the next data refresh.
    private setupJobs() {
        if (this._integrator == null) return;
        DBHelper.saveMainContext();

        let ws = MUIWebApplication.sharedInstance().delegate.webService;
        ws.setupIntegratorJobs(this._integrator, (json: any, error: any) => {
            if (error != null || json == null) {
                AppHelper.showErrorMessage(this, MIOLocalizeString("SETUP_JOBS", "SETUP JOBS"), error ?? MIOLocalizeString("NO_RESPONSE", "NO RESPONSE"));
                return;
            }
            let report = (json["report"] ?? []).join("\n");
            AppHelper.showInfoMessage(this, MIOLocalizeString("SETUP_JOBS", "SETUP JOBS"), report);
        });
    }

    private testConnection() {
        if (this._integrator == null) return;
        // Unsaved edits must be what gets tested: save first, then ask the server.
        DBHelper.saveMainContext();

        let ws = MUIWebApplication.sharedInstance().delegate.webService;
        ws.testIntegrator(this._integrator, (json: any, error: any) => {
            if (error != null || json == null) {
                AppHelper.showErrorMessage(this, MIOLocalizeString("TEST_CONNECTION", "TEST CONNECTION"), error ?? MIOLocalizeString("NO_RESPONSE", "NO RESPONSE"));
                return;
            }
            if (json["ok"] == true) {
                AppHelper.showInfoMessage(this, MIOLocalizeString("CONNECTION_OK", "CONNECTION OK"), json["detail"] ?? "");
            }
            else {
                AppHelper.showErrorMessage(this, MIOLocalizeString("CONNECTION_FAILED", "CONNECTION FAILED"), json["error"] ?? "");
            }
        });
    }

    private addConfig() {
        // What the driver declares shapes the dialog: a role picker when the driver
        // has jobs, so a hand-added config is born complete instead of roleless.
        let manifest = DriverManifests.find(this._integrator != null ? this._integrator.type : null);
        let jobs: any[] = manifest != null ? (manifest["jobs"] ?? []) : [];

        let avc = new MUIAlertViewController();
        avc.initWithTitle(
            MIOLocalizeString("NEW_INTEGRATOR_CONFIG", "NEW INTEGRATOR CONFIG"),
            MIOLocalizeString("ENTER_NAME_ENTITY_AND_TYPE", "ENTER NAME, ENTITY AND TYPE"),
            MUIAlertViewStyle.Default
        );

        avc.addTextFieldWithConfigurationHandler(this, function(textField: MUITextField) {
            textField.setPlaceholderText(MIOLocalizeString("NAME", "NAME"));
        });

        avc.addTextFieldWithConfigurationHandler(this, function(textField: MUITextField) {
            textField.setPlaceholderText(MIOLocalizeString("ENTITY_NAME", "ENTITY NAME"));
        });

        avc.addComboBoxWithConfigurationHandler(this, function(comboBox: MUIComboBox) {
            comboBox.addItem(MIOLocalizeString("NONE", "NONE"), IntegratorConfigType.None);
            comboBox.addItem(MIOLocalizeString("SEMI_AUTOMATIC", "SEMI AUTOMATIC"), IntegratorConfigType.SemiAutomatic);
            comboBox.addItem(MIOLocalizeString("AUTOMATIC", "AUTOMATIC"), IntegratorConfigType.Automatic);
        });

        if (jobs.length > 0) {
            avc.addComboBoxWithConfigurationHandler(this, function(comboBox: MUIComboBox) {
                for (let job of jobs) comboBox.addItem(job["role"], job["role"]);
            });
        }

        avc.addAction(MUIAlertAction.alertActionWithTitle(MIOLocalizeString("ADD", "ADD"), MUIAlertActionStyle.Default, this, function() {
            let name = avc.textFields[0].text.trim();
            let entityName = avc.textFields[1].text.trim();
            let typeValue = avc.comboBoxes[0].getSelectedItem();

            if (name.length == 0 || entityName.length == 0 || typeValue == null) {
                AppHelper.showErrorMessage(this, MIOLocalizeString("ERROR", "ERROR"), MIOLocalizeString("ALL_FIELDS_REQUIRED", "ALL FIELDS ARE REQUIRED"));
                return;
            }

            let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
            let config = MIOEntityDescription.insertNewObjectForEntityForName("IntegratorConfig", ad.managedObjectContext) as IntegratorConfig;
            config.identifier = MIOUUID.UUID().UUIDString;
            config.name = name;
            config.sourceEntityName = entityName;
            config.type = parseInt(typeValue);
            config.integrator = this._integrator;
            config.queueEntityName = "IntegratorSync";
            config.active = true;

            // The mail sender settings belong to the config: one config per sender
            let senderKeys = IntegratorEmailDefaults.senderKeys(this._integrator.type);
            let values: any = senderKeys.length > 0 ? IntegratorEmailDefaults.seed(null, senderKeys) : {};

            // The declared job's identity and error policy travel with the role
            let role = jobs.length > 0 ? avc.comboBoxes[1].getSelectedItem() : null;
            if (role != null) {
                let job = jobs.find((j: any) => j["role"] == role);
                values["role"] = role;
                if (job != null) config.stopOnError = job["stopOnError"] == true;
            }
            if (Object.keys(values).length > 0) config.configValues = values;

            ad.managedObjectContext.save();
        }));

        avc.addAction(MUIAlertAction.alertActionWithTitle(MIOLocalizeString("CANCEL", "CANCEL"), MUIAlertActionStyle.Cancel, null, null));

        this.presentViewController(avc, true);
    }
}
